import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { IModelService } from 'vscode/vscode/vs/editor/common/services/model';
import { ITextModelService } from 'vscode/vscode/vs/editor/common/services/resolverService';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { EnvironmentVariableMutatorType } from '../../../../../platform/terminal/common/environmentVariable.js';
import { registerActiveInstanceAction } from '../../../terminal/browser/terminalActions.js';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

var EnvironmentCollectionProvider_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/environmentChanges/browser/terminal.environmentChanges.contribution";
registerActiveInstanceAction({
    id: "workbench.action.terminal.showEnvironmentContributions" ,
    title: ( localize2WithPath(_moduleId, 0, 'Show Environment Contributions')),
    run: async (activeInstance, c, accessor, arg) => {
        const collection = activeInstance.extEnvironmentVariableCollection;
        if (collection) {
            const scope = arg;
            const instantiationService = accessor.get(IInstantiationService);
            const outputProvider = instantiationService.createInstance(EnvironmentCollectionProvider);
            const editorService = accessor.get(IEditorService);
            const timestamp = ( (new Date())).getTime();
            const scopeDesc = scope?.workspaceFolder ? ` - ${scope.workspaceFolder.name}` : '';
            const textContent = await outputProvider.provideTextContent(( (URI.from({
                scheme: EnvironmentCollectionProvider.scheme,
                path: `Environment changes${scopeDesc}`,
                fragment: describeEnvironmentChanges(collection, scope),
                query: `environment-collection-${timestamp}`
            }))));
            if (textContent) {
                await editorService.openEditor({
                    resource: textContent.uri
                });
            }
        }
    }
});
function describeEnvironmentChanges(collection, scope) {
    let content = `# ${( localizeWithPath(_moduleId, 1, 'Terminal Environment Changes'))}`;
    const globalDescriptions = collection.getDescriptionMap(undefined);
    const workspaceDescriptions = collection.getDescriptionMap(scope);
    for (const [ext, coll] of collection.collections) {
        content += `\n\n## ${( localizeWithPath(_moduleId, 2, 'Extension: {0}', ext))}`;
        content += '\n';
        const globalDescription = globalDescriptions.get(ext);
        if (globalDescription) {
            content += `\n${globalDescription}\n`;
        }
        const workspaceDescription = workspaceDescriptions.get(ext);
        if (workspaceDescription) {
            const workspaceSuffix = globalDescription ? ` (${( localizeWithPath(_moduleId, 3, 'workspace'))})` : '';
            content += `\n${workspaceDescription}${workspaceSuffix}\n`;
        }
        for (const mutator of ( (coll.map.values()))) {
            if (filterScope(mutator, scope) === false) {
                continue;
            }
            content += `\n- \`${mutatorTypeLabel(mutator.type, mutator.value, mutator.variable)}\``;
        }
    }
    return content;
}
function filterScope(mutator, scope) {
    if (!mutator.scope) {
        return true;
    }
    if (mutator.scope.workspaceFolder && scope?.workspaceFolder && mutator.scope.workspaceFolder.index === scope.workspaceFolder.index) {
        return true;
    }
    return false;
}
function mutatorTypeLabel(type, value, variable) {
    switch (type) {
        case EnvironmentVariableMutatorType.Prepend: return `${variable}=${value}\${env:${variable}}`;
        case EnvironmentVariableMutatorType.Append: return `${variable}=\${env:${variable}}${value}`;
        default: return `${variable}=${value}`;
    }
}
let EnvironmentCollectionProvider = class EnvironmentCollectionProvider {
    static { EnvironmentCollectionProvider_1 = this; }
    static { this.scheme = 'ENVIRONMENT_CHANGES_COLLECTION'; }
    constructor(textModelResolverService, _modelService) {
        this._modelService = _modelService;
        textModelResolverService.registerTextModelContentProvider(EnvironmentCollectionProvider_1.scheme, this);
    }
    async provideTextContent(resource) {
        const existing = this._modelService.getModel(resource);
        if (existing && !existing.isDisposed()) {
            return existing;
        }
        return this._modelService.createModel(resource.fragment, { languageId: 'markdown', onDidChange: Event.None }, resource, false);
    }
};
EnvironmentCollectionProvider = EnvironmentCollectionProvider_1 = ( (__decorate([
    ( (__param(0, ITextModelService))),
    ( (__param(1, IModelService)))
], EnvironmentCollectionProvider)));
